/** App root — bootstraps AuthStore and wires routes. */
import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {Navigate, useNavigate} from "react-router-dom";

import AuthStore from "/stores/AuthStore";
import {setUnauthorizedHandler} from "/utils/apiClient";
import AppRoutes from "/Routes";
import DialogHost from "/components/ui/DialogHost";

const App: React.FC = observer(() => {
    const navigate = useNavigate();

    useEffect(() => {
        // Bootstrap before installing the handler: an anonymous /me 401 must
        // not redirect visitors off public pages on first visit.
        void AuthStore.bootstrap().finally(() => {
            setUnauthorizedHandler(() => {
                // Redirect to /sign-in only on private routes.
                const path = window.location.pathname;
                if (path.startsWith("/app") || path.startsWith("/onboarding")) {
                    navigate("/sign-in", {replace: true});
                }
            });
        });
    }, [navigate]);

    // Block render until first /me resolves — else on refresh RequireAuth
    // redirects to /sign-in before the cookie session is restored.
    if (!AuthStore.booted) {
        return <div style={{padding: 40, textAlign: "center"}}>Loading…</div>;
    }

    return (
        <>
            <AppRoutes />
            <DialogHost />
        </>
    );
});

export const RequireAuth: React.FC<{children: React.ReactNode}> = observer(({children}) => {
    // App-level gate waits for booted, so `developer` is real here.
    if (!AuthStore.isAuthenticated) return <Navigate to="/sign-in" replace />;
    return <>{children}</>;
});

export default App;
